import { useMutation, useQueryClient } from '@tanstack/react-query';
import { OverlappingSourcingPurchaseResponse } from 'api-clients/monolith';
import _uniq from 'lodash/uniq';
import { useIntl } from 'react-intl';

import { validateSourcingPurchases } from '@/api-clients/validate-sourcing-purchases';
import { useToaster } from '@/components/base/toaster';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { POOL } from '@/types/channels';

export const POOL_CONFLICTS_QUERY_KEY = ['pool', 'conflicts'] as const;

export const usePoolForm = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useToaster();
  const {
    hookForm,
    setPoolOpeningsWithConflicts,
    poolOpeningsWithConflicts,
    selectedChannelSlugs,
    setSelectedOpeningIds,
  } = useJobPostingContext();
  const { setError, getValues, setValue, clearErrors } = hookForm;

  const conflictsErrorMessage = (count: number) =>
    intl.formatMessage(
      {
        defaultMessage:
          '{count, plural, one {1 opening} other {{count} openings}} have campaigns that overlap.',
        description: 'Error message for overlapping sourcing purchases',
      },
      { count },
    );

  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => {
      const endAt = getValues('pool.endAt');

      return validateSourcingPurchases({
        channel_slug: POOL,
        start_date: getValues('pool.startsAt'),
        end_date: endAt,
        opening_ids: Object.keys(getValues('pool.openingMessages')),
      });
    },
    onSuccess: (data: OverlappingSourcingPurchaseResponse) => {
      const { conflicts } = data;
      const uniqueOpeningsWithConflicts = _uniq(
        conflicts.map(conflict => conflict.opening_id),
      );

      if (uniqueOpeningsWithConflicts.length > 0) {
        queryClient.setQueryData(POOL_CONFLICTS_QUERY_KEY, conflicts);
        setPoolOpeningsWithConflicts(uniqueOpeningsWithConflicts);

        notifyError({
          message: conflictsErrorMessage(uniqueOpeningsWithConflicts.length),
        });
        setError(`pool.endAt`, {
          message: conflictsErrorMessage(uniqueOpeningsWithConflicts.length),
        });
      } else {
        queryClient.setQueryData(POOL_CONFLICTS_QUERY_KEY, undefined);
        setPoolOpeningsWithConflicts(undefined);

        notifySuccess({
          message: intl.formatMessage({
            defaultMessage: 'Changes saved successfully',
            description: 'Success message for saving campaign details',
          }),
        });
      }
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to save changes',
          description: 'Error message for saving campaign details',
        }),
      });
    },
  });

  /*
   * Validate the overlapping pool purchases by calling the API to validate if there are any overlapping purchases
   * Returns true if there are no conflicts, false otherwise
   */
  const validateOverlappingPoolPurchasesAsync = async (): Promise<boolean> => {
    try {
      const response = await mutateAsync();
      const hasConflicts = response?.conflicts && response.conflicts.length > 0;
      return !hasConflicts; // Return true if there are no conflicts
    } catch {
      return false;
    }
  };

  /* Remove the opening from the pool when user deletes it */
  const removeOpeningFromPool = (openingId: string) => {
    // Remove the opening from the pool openings list
    const poolOpenings = getValues('pool.openingMessages');
    const updatedPoolOpenings = { ...poolOpenings };
    delete updatedPoolOpenings[openingId];
    setValue('pool.openingMessages', updatedPoolOpenings);

    // If Pool is the only channel, remove the opening from the list of selected openings
    if (selectedChannelSlugs.length === 1)
      setSelectedOpeningIds(prev => prev.filter(id => id !== openingId));
  };

  /* Remove the conflicting opening from the channel when user deletes it */
  const removeConflictingOpening = (openingId: string) => {
    // Remove the opening from the conflicts list
    const updatedConflicts = poolOpeningsWithConflicts?.filter(
      id => id !== openingId,
    );
    setPoolOpeningsWithConflicts(updatedConflicts);

    removeOpeningFromPool(openingId);

    // If there are still conflicts, set the error message
    if (updatedConflicts && updatedConflicts.length > 0) {
      setError(`pool.endAt`, {
        message: conflictsErrorMessage(updatedConflicts?.length ?? 0),
      });
    } else {
      clearErrors(`pool.endAt`);
    }
  };

  return {
    validateOverlappingPoolPurchasesAsync,
    isValidatingPoolPurchases: isPending,
    // Returns the conflicts from the query client so that it can be consumed from different components
    conflicts: queryClient.getQueryData<
      OverlappingSourcingPurchaseResponse['conflicts']
    >(POOL_CONFLICTS_QUERY_KEY),
    removeConflictingOpening,
    removeOpeningFromPool,
  };
};
